import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../common/Icon';

// ─── PlaceholderQR ─────────────────────────────────────────────────────────

/**
 * Decorative 21×21 placeholder QR code. Looks structurally like a real
 * QR (three corner finder patterns + deterministic pseudo-random body
 * derived from the URL's char codes) but doesn't actually encode the
 * URL. Production should swap this for `qrcode.react` or similar.
 */
export function PlaceholderQR({ url }: { url: string }) {
  const SIZE = 21;
  const cells: boolean[] = [];
  // Seeded from url char codes — deterministic per url
  let seed = 0;
  for (let i = 0; i < url.length; i++) seed = (seed * 31 + url.charCodeAt(i)) >>> 0;
  for (let i = 0; i < SIZE * SIZE; i++) {
    seed = (seed * 1103515245 + 12345) >>> 0;
    cells.push((seed & 0xff) > 128);
  }
  // Force the three QR finder patterns (top-left, top-right, bottom-left)
  const setFinder = (cx: number, cy: number) => {
    for (let dy = 0; dy < 7; dy++) {
      for (let dx = 0; dx < 7; dx++) {
        const isOuter = dx === 0 || dx === 6 || dy === 0 || dy === 6;
        const isInner = dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4;
        cells[(cy + dy) * SIZE + (cx + dx)] = isOuter || isInner;
      }
    }
    // Quiet zone around finder
    for (let dy = -1; dy <= 7; dy++) {
      for (let dx = -1; dx <= 7; dx++) {
        if (dx === -1 || dx === 7 || dy === -1 || dy === 7) {
          const x = cx + dx;
          const y = cy + dy;
          if (x >= 0 && x < SIZE && y >= 0 && y < SIZE && !(dx >= 0 && dx <= 6 && dy >= 0 && dy <= 6)) {
            cells[y * SIZE + x] = false;
          }
        }
      }
    }
  };
  setFinder(0, 0);
  setFinder(SIZE - 7, 0);
  setFinder(0, SIZE - 7);

  return (
    <div
      className="grid"
      style={{
        gridTemplateColumns: `repeat(${SIZE}, 8px)`,
        gridTemplateRows: `repeat(${SIZE}, 8px)`,
        gap: 0,
      }}
      aria-label="Team QR code (decorative placeholder)"
    >
      {cells.map((on, i) => (
        <div key={i} className={on ? 'bg-gray-900' : 'bg-white'} />
      ))}
    </div>
  );
}

// ─── TiltCard ──────────────────────────────────────────────────────────────

interface TiltCardProps {
  children: ReactNode;
  /** Localized labels for the motion-permission button. Defaults
   * are English; pass i18n strings from the call site. */
  motionLabels?: {
    enable:  string;
    pending: string;
    denied:  string;
  };
}

type RequestableDeviceOrientationEvent = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<'granted' | 'denied'>;
};

/**
 * TiltCard — interactive 3D card wrapper with mouse-driven tilt
 * (and gyroscope-driven tilt on mobile after permission is granted)
 * plus a holographic shine overlay. Inspired by reactbits.dev's
 * ProfileCard.
 *
 * Effects layered on top of `children`:
 *   1. **3D tilt**: cursor X/Y or device gamma/beta → `rotateY` / `rotateX`
 *   2. **Radial spotlight**: a soft white glow that tracks the input
 *   3. **Holographic gradient**: a hue-shifting reflection band
 *   4. **Inertia transition**: 120ms ease-out in / 800ms cubic-bezier out
 *   5. **Perspective parent**: `transform-style: preserve-3d`
 */
export function TiltCard({
  children,
  motionLabels = { enable: 'Tilt to spin', pending: 'Requesting…', denied: 'Motion denied' },
}: TiltCardProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState({ x: 50, y: 50 });
  const [active, setActive] = useState(false);
  const [motionState, setMotionState] = useState<'idle' | 'pending' | 'on' | 'denied' | 'unsupported'>('idle');
  const betaOffsetRef = useRef<number | null>(null);

  const enableMotion = async () => {
    if (typeof window === 'undefined' || typeof DeviceOrientationEvent === 'undefined') {
      setMotionState('unsupported');
      return;
    }
    setMotionState('pending');
    const ReqEvent = DeviceOrientationEvent as RequestableDeviceOrientationEvent;
    if (typeof ReqEvent.requestPermission === 'function') {
      try {
        const result = await ReqEvent.requestPermission();
        setMotionState(result === 'granted' ? 'on' : 'denied');
      } catch {
        setMotionState('denied');
      }
    } else {
      setMotionState('on');
    }
  };

  useEffect(() => {
    if (motionState !== 'on') return;
    const handler = (e: DeviceOrientationEvent) => {
      const gamma = e.gamma ?? 0;
      const beta  = e.beta  ?? 0;
      if (betaOffsetRef.current == null) betaOffsetRef.current = beta;
      const adjustedBeta = beta - betaOffsetRef.current;
      const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
      const xPct = 50 + (clamp(gamma, -30, 30) / 30) * 50;
      const yPct = 50 + (clamp(adjustedBeta, -30, 30) / 30) * 50;
      setCoords({ x: xPct, y: yPct });
      setActive(true);
    };
    window.addEventListener('deviceorientation', handler, true);
    return () => window.removeEventListener('deviceorientation', handler, true);
  }, [motionState]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (motionState === 'on') return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setCoords({ x, y });
  };

  const rotateY = ((coords.x - 50) / 50) * 10;
  const rotateX = -((coords.y - 50) / 50) * 10;

  return (
    <div style={{ perspective: '1000px' }}>
      <div
        ref={ref}
        onMouseEnter={() => setActive(true)}
        onMouseLeave={() => {
          setActive(false);
          setCoords({ x: 50, y: 50 });
        }}
        onMouseMove={handleMouseMove}
        className="relative"
        style={{
          transformStyle: 'preserve-3d',
          transform: active
            ? `rotateY(${rotateY}deg) rotateX(${rotateX}deg) scale(1.02)`
            : 'rotateY(0) rotateX(0) scale(1)',
          transition: active
            ? 'transform 120ms ease-out'
            : 'transform 800ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <div className="relative z-0">{children}</div>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-lg overflow-hidden mix-blend-overlay"
          style={{
            background: `radial-gradient(circle at ${coords.x}% ${coords.y}%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.0) 45%)`,
            opacity: active ? 1 : 0,
            transition: 'opacity 200ms ease-out',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-lg overflow-hidden mix-blend-color-dodge"
          style={{
            background: `linear-gradient(${(coords.x + coords.y) * 1.8}deg,
              rgba(255, 0, 122, 0.20) 0%,
              rgba(0, 217, 160, 0.20) 25%,
              rgba(0, 122, 255, 0.20) 50%,
              rgba(255, 200, 0, 0.20) 75%,
              rgba(255, 0, 200, 0.20) 100%
            )`,
            opacity: active ? 0.75 : 0,
            transition: 'opacity 250ms ease-out',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-lg ring-1 ring-white/30"
          style={{
            opacity: active ? 1 : 0,
            transition: 'opacity 300ms ease-out',
          }}
        />
      </div>
      {motionState !== 'on' && motionState !== 'unsupported' && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={enableMotion}
            disabled={motionState === 'pending'}
            className="
              px-3 py-1 rounded-full text-[11px] font-medium
              bg-white/[0.10] text-white/80
              hover:bg-white/[0.16]
              disabled:opacity-40
              backdrop-blur-sm
              transition-colors
              flex items-center gap-1.5
            "
          >
            <Icon name="screen_rotation" size={13} />
            {motionState === 'pending'
              ? motionLabels.pending
              : motionState === 'denied'
              ? motionLabels.denied
              : motionLabels.enable}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── CameraScanner ─────────────────────────────────────────────────────────

/**
 * CameraScanner — full-frame faux camera scanner. Requests the
 * device camera via `navigator.mediaDevices.getUserMedia({ video })`
 * and pipes the stream into a `<video>` element. Overlays the
 * scanning frame (4 corner brackets) and an animated scan line that
 * bounces top↔bottom inside the frame.
 *
 * Three runtime states:
 *   - `loading`  — waiting on the user's permission grant
 *   - `streaming` — camera live, showing video feed under the frame
 *   - `denied`   — permission denied, getUserMedia not available, or
 *                  http (camera APIs require https). Falls back to a
 *                  black background + an info message under the same
 *                  scan frame so the layout stays stable.
 */
export function CameraScanner({ hint }: { hint: string }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [status, setStatus] = useState<'loading' | 'streaming' | 'denied'>('loading');
  const [errMsg, setErrMsg] = useState<string>('');

  useEffect(() => {
    let active = true;
    let stream: MediaStream | null = null;

    const start = async () => {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        if (active) {
          setStatus('denied');
          setErrMsg('Camera API unavailable');
        }
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setStatus('streaming');
      } catch (e) {
        if (active) {
          setStatus('denied');
          setErrMsg(e instanceof Error ? e.message : 'Camera access denied');
        }
      }
    };
    void start();

    return () => {
      active = false;
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, []);

  return (
    <div className="absolute inset-0">
      <style>{`
        @keyframes qrScanLine {
          0%   { top: 0%;    opacity: 0.4; }
          10%  { opacity: 1; }
          50%  { top: 100%;  opacity: 1; }
          60%  { opacity: 0.4; }
          100% { top: 0%;    opacity: 0.4; }
        }
        .qr-scan-line {
          animation: qrScanLine 2.4s ease-in-out infinite;
        }
      `}</style>

      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      />
      {status !== 'streaming' && <div className="absolute inset-0 bg-black" />}
      <div className="absolute inset-0 bg-black/40" />

      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="relative w-56 h-56">
          <span className="absolute top-0 left-0  w-8 h-8 border-t-2 border-l-2 border-white" />
          <span className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-white" />
          <span className="absolute bottom-0 left-0  w-8 h-8 border-b-2 border-l-2 border-white" />
          <span className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-white" />
          <span
            className="qr-scan-line absolute left-2 right-2 h-px bg-primary shadow-[0_0_8px_2px_rgba(0,217,160,0.6)]"
            style={{ top: '0%' }}
          />
        </div>
        <div className="mt-6 text-xs text-white/70 text-center px-8">
          {hint}
        </div>
        {status === 'loading' && (
          <div className="mt-2 text-[10px] text-white/40">Requesting camera…</div>
        )}
        {status === 'denied' && (
          <div className="mt-2 text-[10px] text-white/40 max-w-xs text-center">
            {errMsg || 'Camera unavailable — grant permission and reload.'}
          </div>
        )}
      </div>
    </div>
  );
}
